import { OfflineOrder } from '../../domain/offlineOrder/OfflineOrder';
import { OfflineStatus } from '../../domain/offlineOrder/offlineOrder.types';
import { OfflineOrderProduct } from '../../domain/offlineOrder/orderProduct/OfflineOrderProduct';
import { OfflineGridItem } from '../../domain/offlineOrder/orderProduct/offlineOrderProduct.types';
import { OfflineSkuPriceHandler } from '../utils/OfflineSkuPriceHandler.util';
import { forceTextInCSV } from './utils/csv.utilts';

export class OfflineOrderCsvBuilder {
  constructor(private readonly orders: OfflineOrder[]) {}

  build(): Blob {
    const csvHeader = this.offlineCsvColumnMapper.map((col) => col.columnName).join(';');

    const csvRows = this.orders.flatMap((order) => this.buildOrderRow(order));

    const csvString = [csvHeader, ...csvRows].join('\n');

    const csvStringWithBOM = this.addBOMToCsvString(csvString);

    return this.csvToBlob(csvStringWithBOM);
  }

  private csvToBlob(csvString: string): Blob {
    return new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  }

  private addBOMToCsvString(csvString: string): string {
    const utf8BOM = '\uFEFF';
    return `${utf8BOM}${csvString}`;
  }

  private mapProductToCsvRow(
    order: OfflineOrder,
    product: OfflineOrderProduct,
    gridItem: OfflineGridItem,
  ) {
    return this.offlineCsvColumnMapper
      .map((col) => col.getValue(order, product, gridItem) ?? '')
      .join(';');
  }

  private buildOrderRow(order: OfflineOrder): string[] {
    const csvColumnValues = order.products?.flatMap((orderProduct) => {
      const gridsWithQuantity = orderProduct.grid.filter((gridItem) => gridItem.quantity);

      const gridsToCsv = gridsWithQuantity.length ? gridsWithQuantity : orderProduct.grid;

      return gridsToCsv.map((gridItem) => this.mapProductToCsvRow(order, orderProduct, gridItem));
    });

    return csvColumnValues || [];
  }

  private readonly offlineCsvColumnMapper = [
    {
      columnPosition: 0,
      columnName: 'codigo_pedido',
      getValue: (order: OfflineOrder) => forceTextInCSV(order.code.toString()),
    },
    {
      columnPosition: 1,
      columnName: 'status_pedido',
      getValue: () => OfflineStatus.OFFLINE,
    },
    {
      columnPosition: 2,
      columnName: 'codigo_grupo_vendedor',
      getValue: (order: OfflineOrder) => forceTextInCSV(order.salesRepresentative?.code),
    },
    {
      columnPosition: 3,
      columnName: 'nome_vendedor',
      getValue: (order: OfflineOrder) => order.seller?.name,
    },
    {
      columnPosition: 4,
      columnName: 'email_vendedor',
      getValue: (order: OfflineOrder) => order.seller?.email,
    },
    {
      columnPosition: 7,
      columnName: 'codigo_cliente',
      getValue: (order: OfflineOrder) => forceTextInCSV(order.customer?.code),
    },
    {
      columnPosition: 8,
      columnName: 'nome_cliente',
      getValue: (order: OfflineOrder) => order.customer?.companyName,
    },
    {
      columnPosition: 9,
      columnName: 'nome_fantasia',
      getValue: (order: OfflineOrder) => order.customer?.commercialName,
    },
    {
      columnPosition: 10,
      columnName: 'cpf_cnpj',
      getValue: (order: OfflineOrder) => order.customer?.cpfcnpj,
    },
    {
      columnPosition: 11,
      columnName: 'codigo_produto',
      getValue: (_: OfflineOrder, product: OfflineOrderProduct) =>
        forceTextInCSV(product.product.code),
    },
    {
      columnPosition: 12,
      columnName: 'nome_produto',
      getValue: (_: OfflineOrder, product: OfflineOrderProduct) => product.product.name,
    },
    {
      columnPosition: 13,
      columnName: 'codigo_sku',
      getValue: (_: OfflineOrder, __: OfflineOrderProduct, gridItem: OfflineGridItem) =>
        forceTextInCSV(gridItem.sku.code),
    },
    {
      columnPosition: 14,
      columnName: 'codigo_cor',
      getValue: (_: OfflineOrder, product: OfflineOrderProduct) =>
        forceTextInCSV(product.productColor.code),
    },
    {
      columnPosition: 15,
      columnName: 'nome_cor',
      getValue: (_: OfflineOrder, product: OfflineOrderProduct) => product.productColor.name,
    },
    {
      columnPosition: 16,
      columnName: 'tamanho',
      getValue: (_: OfflineOrder, __: OfflineOrderProduct, gridItem: OfflineGridItem) =>
        gridItem.size.name,
    },
    {
      columnPosition: 17,
      columnName: 'disponibilidade',
      getValue: (_: OfflineOrder, product: OfflineOrderProduct) => product.availability,
    },
    {
      columnPosition: 18,
      columnName: 'codigo_janela_entrega',
      getValue: (_: OfflineOrder, product: OfflineOrderProduct) =>
        forceTextInCSV(product.deliveryWindow?.code),
    },
    {
      columnPosition: 19,
      columnName: 'nome_janela_entrega',
      getValue: (_: OfflineOrder, product: OfflineOrderProduct) => product.deliveryWindow?.name,
    },
    {
      columnPosition: 20,
      columnName: 'quantidade_sku',
      getValue: (_: OfflineOrder, __: OfflineOrderProduct, gridItem: OfflineGridItem) =>
        gridItem.quantity,
    },
    {
      columnPosition: 21,
      columnName: 'preco_unitario_sku_original',
      getValue: (_: OfflineOrder, product: OfflineOrderProduct, gridItem: OfflineGridItem) =>
        OfflineSkuPriceHandler.getPricesBySize(
          product.metadata.productColorPriceInfo,
          gridItem.size.id,
        ).wholesalePrice,
    },
    {
      columnPosition: 22,
      columnName: 'preco_unitario_sku',
      getValue: (_: OfflineOrder, product: OfflineOrderProduct, gridItem: OfflineGridItem) =>
        OfflineSkuPriceHandler.getPricesBySize(
          product.metadata.productColorPriceInfo,
          gridItem.size.id,
        ).unitPrice,
    },
    {
      columnPosition: 23,
      columnName: 'total_itens_pedido',
      getValue: (order: OfflineOrder) => order.totalItems,
    },
    {
      columnPosition: 24,
      columnName: 'total_pecas_pedido',
      getValue: (order: OfflineOrder) => order.totalPieces,
    },
    {
      columnPosition: 25,
      columnName: 'valor_bruto_pedido',
      getValue: (order: OfflineOrder) => order.grossValue,
    },
    {
      columnPosition: 26,
      columnName: 'valor_liquido_pedido',
      getValue: (order: OfflineOrder) => order.totalValue,
    },
    {
      columnPosition: 26.3,
      columnName: 'tabela_de_preco_nome',
      getValue: (order: OfflineOrder) => order.priceTable.name,
    },
    {
      columnPosition: 26.5,
      columnName: 'tabela_de_preco_codigo',
      getValue: (order: OfflineOrder) => forceTextInCSV(order.priceTable.code),
    },
    {
      columnPosition: 27,
      columnName: 'observacao',
      getValue: (order: OfflineOrder) => order.observation,
    },
  ].sort((a, b) => a.columnPosition - b.columnPosition);
}
